import { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
  MutationCache,
} from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import SentryRouteBreadcrumb from "./components/SentryRouteBreadcrumb";
import { addSentryBreadcrumb } from "@/lib/sentry";
import { HelmetProvider } from "react-helmet-async";
import { AdminAuth } from "./components/AdminAuth";
import AnalyticsTracker from "./hooks/useAnalyticsPageviews";
import { lazyWithRetry as lazy } from "@/utils/lazyWithRetry";
import RouteSkeleton from "./components/loading/RouteSkeleton";
import HeritageFallback from "./components/loading/HeritageFallback";
import HeritageErrorFallback from "./components/loading/HeritageErrorFallback";
import ChunkErrorBoundary from "./components/loading/ChunkErrorBoundary";
import FallbackTimer from "./components/loading/FallbackTimer";
import { useLocation } from "react-router-dom";
import Layout from "./components/layout/Layout";
import { AdminHomeButton } from "./components/AdminHomeButton";
/**
 * Drop-in component that redirects /yo-deed-tracker/* → /yo-historic-land-documents/*
 * preserving the rest of the path and query string.
 */
const LegacyDeedTrackerRedirect = () => {
  const loc = useLocation();
  const newPath = loc.pathname.replace(/^\/yo-deed-tracker/, "/yo-historic-land-documents");
  return <Navigate to={`${newPath}${loc.search}${loc.hash}`} replace />;
};

// Eager load the main landing page for best FCP
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Login from "./pages/Login";
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
// Heritage CMS renderer is ~22KB and only used on a handful of routes —
// lazy-load it so it stays out of the initial bundle.
const HeritagePageRendererLazy = lazy(() => import("./components/heritage/HeritagePageRenderer"));
const HeritageRoute = ({ slug }: { slug: string }) => (
  <ChunkErrorBoundary
    resetKey={slug}
    fallback={({ retry, hardReload, error }) => (
      <HeritageErrorFallback
        onRetry={retry}
        onHardReload={hardReload}
        error={error}
      />
    )}
  >
    <Suspense fallback={<HeritageFallback />}>
      <HeritagePageRendererLazy slug={slug} />
    </Suspense>
  </ChunkErrorBoundary>
);
const LegacyExoticRedirect = lazy(() => import("./pages/LegacyExoticRedirect"));
const WildlifeHeritage = lazy(() => import("./pages/WildlifeHeritage"));
const NativeAmericanHeritage = lazy(() => import("./pages/NativeAmericanHeritage"));

// Lazy load all other pages (with one-time retry on stale chunk failures)
const Museum = lazy(() => import("./pages/Museum"));
const Exhibits = lazy(() => import("./pages/Exhibits"));
const OnDisplay = lazy(() => import("./pages/OnDisplay"));
const ExhibitDetail = lazy(() => import("./pages/ExhibitDetail"));
const CmsExhibitDetail = lazy(() => import("./pages/CmsExhibitDetail"));
const ExhibitDetailDispatcher = lazy(() => import("./pages/ExhibitDetailDispatcher"));
const Collections = lazy(() => import("./pages/Collections"));
const CollectionDetail = lazy(() => import("./pages/CollectionDetail"));
const FirearmsCollection = lazy(() => import("./pages/FirearmsCollection"));
const ColtModel1878Scholarship = lazy(() => import("./pages/ColtModel1878Scholarship"));
const AtenBrothers = lazy(() => import("./pages/AtenBrothers"));
const HClayDahlberg = lazy(() => import("./pages/HClayDahlberg"));
const TexasRangers = lazy(() => import("./pages/TexasRangers"));
const TexasRangerHall = lazy(() => import("./pages/TexasRangerHall"));
const RanchHistory = lazy(() => import("./pages/RanchHistory"));
const RanchLandmarks = lazy(() => import("./pages/RanchLandmarks"));
const RanchMap = lazy(() => import("./pages/RanchMap"));
const SaddleHistory = lazy(() => import("./pages/SaddleHistory"));
const LonghornPreservation = lazy(() => import("./pages/LonghornPreservation"));
const FirstHuntingLease = lazy(() => import("./pages/FirstHuntingLease"));
const LandHistory = lazy(() => import("./pages/LandHistory"));
const TractHistory = lazy(() => import("./pages/TractHistory"));
const TractArchive = lazy(() => import("./pages/TractArchive"));
const DeedArchive = lazy(() => import("./pages/DeedArchive"));
const TexasLonghornHeritage = lazy(() => import("./pages/TexasLonghornHeritage"));
const LonghornPreservationPioneers = lazy(() => import("./pages/LonghornPreservationPioneers"));
const LonghornGallery = lazy(() => import("./pages/LonghornGallery"));
const HistoricalGalleryPage = lazy(() => import("./pages/HistoricalGalleryPage"));
const HistoricalFiguresIndex = lazy(() => import("./pages/HistoricalFiguresIndex"));
const TLBAAFounders = lazy(() => import("./pages/TLBAAFounders"));
const TLBAAGrowthTimeline = lazy(() => import("./pages/TLBAAGrowthTimeline"));
const WalterRiedel = lazy(() => import("./pages/tlbaa-founders/WalterRiedel"));
const TravisMarks = lazy(() => import("./pages/tlbaa-founders/TravisMarks"));
const OswaldSauer = lazy(() => import("./pages/tlbaa-founders/OswaldSauer"));
const ElvinBlevins = lazy(() => import("./pages/tlbaa-founders/ElvinBlevins"));
const ArthurBright = lazy(() => import("./pages/tlbaa-founders/ArthurBright"));
const WalterBScott = lazy(() => import("./pages/tlbaa-founders/WalterBScott"));
const CharlesSchreinerIII = lazy(() => import("./pages/CharlesSchreinerIII"));
const CaptainSchreiner = lazy(() => import("./pages/CaptainSchreiner"));
const SchreinerDynasty = lazy(() => import("./pages/SchreinerDynasty"));
const SchreinerFamilyTreePage = lazy(() => import("./pages/SchreinerFamilyTreePage"));
const SchreinerFamilyPhotos = lazy(() => import("./pages/SchreinerFamilyPhotos"));
const VitalRecords = lazy(() => import("./pages/VitalRecords"));
const VitalRecordsWanted = lazy(() => import("./pages/VitalRecordsWanted"));
const YOFiscalYear1983 = lazy(() => import("./pages/YOFiscalYear1983"));
const MyrtleSchreiner = lazy(() => import("./pages/MyrtleSchreiner"));
const WalterSchreiner = lazy(() => import("./pages/WalterSchreiner"));
const LouisSchreiner = lazy(() => import("./pages/LouisSchreiner"));
const ACSchreiner = lazy(() => import("./pages/ACSchreiner"));
const GusSchreiner = lazy(() => import("./pages/GusSchreiner"));
const The1917Division = lazy(() => import("./pages/The1917Division"));
const CharlesSchreinerIV = lazy(() => import("./pages/CharlesSchreinerIV"));
const WalterSchreinerIV = lazy(() => import("./pages/WalterSchreinerIV"));
const AdminSetup = lazy(() => import("./pages/AdminSetup"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminGuests = lazy(() => import("./pages/AdminGuests"));
const AdminLedgerReview = lazy(() => import("./pages/AdminLedgerReview"));
const AdminAcquisitions = lazy(() => import("./pages/AdminAcquisitions"));
const AdminAcquisitionsFlagQueue = lazy(() => import("./pages/AdminAcquisitionsFlagQueue"));
const AdminAcquisitionsBulkCorrections = lazy(() => import("./pages/AdminAcquisitionsBulkCorrections"));
const AdminAutoImageBackfill = lazy(() => import("./pages/AdminAutoImageBackfill"));
const AdminMissingPhotosQueue = lazy(() => import("./pages/AdminMissingPhotosQueue"));
const GusSchreinerFourthGen = lazy(() => import("./pages/GusSchreinerFourthGen"));
const LouieSchreinerII = lazy(() => import("./pages/LouieSchreinerII"));
const SchreinerFamilyAlbum = lazy(() => import("./pages/SchreinerFamilyAlbum"));
const AudreySchreiner = lazy(() => import("./pages/AudreySchreiner"));
const MarionVanDenburgh = lazy(() => import("./pages/MarionVanDenburgh"));
const PatriciaAnnLopez = lazy(() => import("./pages/PatriciaAnnLopez"));
const LenaSchreiner = lazy(() => import("./pages/LenaSchreiner"));
const FrancesSchreiner = lazy(() => import("./pages/FrancesSchreiner"));
const MimiSchreiner = lazy(() => import("./pages/MimiSchreiner"));
const MadelinaSchreiner = lazy(() => import("./pages/MadelinaSchreiner"));
const RanchCharacters = lazy(() => import("./pages/RanchCharacters"));
const JesseGarza = lazy(() => import("./pages/JesseGarza"));
const ButtonForehand = lazy(() => import("./pages/ButtonForehand"));
const JeromeAlexander = lazy(() => import("./pages/JeromeAlexander"));
const GuidesAndLegends = lazy(() => import("./pages/GuidesAndLegends"));
const CharlieSeale = lazy(() => import("./pages/CharlieSeale"));
const JimmyPeril = lazy(() => import("./pages/JimmyPeril"));
const BobJayDarragh = lazy(() => import("./pages/BobJayDarragh"));
const DavidNesbitt = lazy(() => import("./pages/DavidNesbitt"));
const LouieMecey = lazy(() => import("./pages/LouieMecey"));
const ClydeFischer = lazy(() => import("./pages/ClydeFischer"));
const JackPotter = lazy(() => import("./pages/JackPotter"));
const HarveyGoff = lazy(() => import("./pages/HarveyGoff"));
const RedAlexander = lazy(() => import("./pages/RedAlexander"));
const PhotoSearch = lazy(() => import("./pages/PhotoSearch"));
const ClarenceHyde = lazy(() => import("./pages/ClarenceHyde"));
const CaptainBobSnow = lazy(() => import("./pages/CaptainBobSnow"));
const BobSnowPhotoRelated = lazy(() => import("./pages/BobSnowPhotoRelated"));
const AdamCarter = lazy(() => import("./pages/AdamCarter"));
const GarlandScogin = lazy(() => import("./pages/GarlandScogin"));
const MacHyde = lazy(() => import("./pages/MacHyde"));
const GarzaFamily = lazy(() => import("./pages/GarzaFamily"));
const DrRussellThomas = lazy(() => import("./pages/DrRussellThomas"));
const ExoticSpecies = lazy(() => import("./pages/ExoticSpecies"));
const GiraffeSpecies = lazy(() => import("./pages/GiraffeSpecies"));
const YOIbexSpecies = lazy(() => import("./pages/YOIbexSpecies"));
const WildlifeSafari = lazy(() => import("./pages/WildlifeSafari"));
const HuntingHeritage = lazy(() => import("./pages/HuntingHeritage"));
const TrophyRecordsHunters = lazy(() => import("./pages/TrophyRecordsHunters"));
const FamousVisitors = lazy(() => import("./pages/FamousVisitors"));
const VisitorTimeline = lazy(() => import("./pages/VisitorTimeline"));
const VisitorFaceTimeline = lazy(() => import("./pages/VisitorFaceTimeline"));
const VisitorGallery = lazy(() => import("./pages/VisitorGallery"));
const VisitorMap = lazy(() => import("./pages/VisitorMap"));
const VisitorDirectory = lazy(() => import("./pages/VisitorDirectory"));
const VisitorBiography = lazy(() => import("./pages/VisitorBiography"));
const VisitorPersonTimeline = lazy(() => import("./pages/VisitorPersonTimeline"));
const VisitorExhibits = lazy(() => import("./pages/VisitorExhibits"));
const AstronautRoom = lazy(() => import("./pages/AstronautRoom"));
const JoeEngle = lazy(() => import("./pages/JoeEngle"));
const ChuckYeager = lazy(() => import("./pages/ChuckYeager"));
const DougWalker = lazy(() => import("./pages/DougWalker"));
const RonSchara = lazy(() => import("./pages/RonSchara"));
const NormanSchwarzkopf = lazy(() => import("./pages/NormanSchwarzkopf"));
const LyndonJohnson = lazy(() => import("./pages/LyndonJohnson"));
const JohnConnally = lazy(() => import("./pages/JohnConnally"));
const DolphBriscoe = lazy(() => import("./pages/DolphBriscoe"));
const AceReid = lazy(() => import("./pages/AceReid"));
const JoeBeeler = lazy(() => import("./pages/JoeBeeler"));
const FredGipson = lazy(() => import("./pages/FredGipson"));
const HondoCrouch = lazy(() => import("./pages/HondoCrouch"));
const HollywoodMedia = lazy(() => import("./pages/HollywoodMedia"));
const NewsMedia = lazy(() => import("./pages/NewsMedia"));
const LiteraryLegacy = lazy(() => import("./pages/LiteraryLegacy"));
const JFrankDobie = lazy(() => import("./pages/JFrankDobie"));
const JamesMichener = lazy(() => import("./pages/JamesMichener"));
const ElliottMeans = lazy(() => import("./pages/ElliottMeans"));
const MichaelWayne = lazy(() => import("./pages/MichaelWayne"));
const PatrickWayne = lazy(() => import("./pages/PatrickWayne"));
const AudieMurphy = lazy(() => import("./pages/AudieMurphy"));
const ThomasHadenChurch = lazy(() => import("./pages/ThomasHadenChurch"));
const CultureAndFame = lazy(() => import("./pages/CultureAndFame"));
const YOSamHouston = lazy(() => import("./pages/YOSamHouston"));
const YOCarmela = lazy(() => import("./pages/YOCarmela"));
const BevoVII = lazy(() => import("./pages/BevoVII"));
const Heritage = lazy(() => import("./pages/Heritage"));
const Stewardship = lazy(() => import("./pages/Stewardship"));
const Visit = lazy(() => import("./pages/Visit"));
const VisitStatus = lazy(() => import("./pages/VisitStatus"));
const Contact = lazy(() => import("./pages/Contact"));
const ArtifactDetail = lazy(() => import("./pages/ArtifactDetail"));
const ArtifactByIdRedirect = lazy(() =>
  import("./pages/ArtifactDetail").then((m) => ({ default: m.ArtifactByIdRedirect })),
);
const TexasDpsPamphletDetail = lazy(() => import("./pages/artifacts/TexasDpsPamphletDetail"));
const AdminExclusions = lazy(() => import("./pages/AdminExclusions"));
const AdminDocImportTriage = lazy(() => import("./pages/AdminDocImportTriage"));
const AdminDocPageLinks = lazy(() => import("./pages/AdminDocPageLinks"));
const AdminDocImportDuplicates = lazy(() => import("./pages/AdminDocImportDuplicates"));
const AdminSidewaysScans = lazy(() => import("./pages/AdminSidewaysScans"));
const AdminContentIndexing = lazy(() => import("./pages/AdminContentIndexing"));
const AdminAnalyzeImages = lazy(() => import("./pages/AdminAnalyzeImages"));
const AdminArchiveReviewQueue = lazy(() => import("./pages/AdminArchiveReviewQueue"));
const AdminArchiveToCms = lazy(() => import("./pages/AdminArchiveToCms"));
const AdminArchive = lazy(() => import("./pages/AdminArchive"));
const AdminArchivePagesBackfill = lazy(() => import("./pages/AdminArchivePagesBackfill"));
const AdminHistoricPhotos = lazy(() => import("./pages/AdminHistoricPhotos"));
const AdminPhotoReview = lazy(() => import("./pages/AdminPhotoReview"));
const AdminPhotoResearchDashboard = lazy(() => import("./pages/AdminPhotoResearchDashboard"));
const AdminPhotoArchive = lazy(() => import("./pages/AdminPhotoArchive"));
const AdminPhotoArchiveViewer = lazy(() => import("./pages/AdminPhotoArchiveViewer"));
const AdminPhotoReviewQueue = lazy(() => import("./pages/AdminPhotoReviewQueue"));
const AdminPhotoClusters = lazy(() => import("./pages/AdminPhotoClusters"));
const AdminPhotoClusterDetail = lazy(() => import("./pages/AdminPhotoClusterDetail"));
const AdminFaceAlertLog = lazy(() => import("./pages/AdminFaceAlertLog"));
const AdminPhotoIdentityQueue = lazy(() => import("./pages/AdminPhotoIdentityQueue"));
const AdminArcFaceReview = lazy(() => import("./pages/AdminArcFaceReview"));
const AdminFaceNameConflicts = lazy(() => import("./pages/AdminFaceNameConflicts"));
const AdminVisitorBiographies = lazy(() => import("./pages/AdminVisitorBiographies"));
const AdminVisitorVitals = lazy(() => import("./pages/AdminVisitorVitals"));
const AdminFaceWorkQueue = lazy(() => import("./pages/AdminFaceWorkQueue"));
const AdminPhotoWorkQueue = lazy(() => import("./pages/AdminPhotoWorkQueue"));
const AdminPhotoRoutes = lazy(() => import("./pages/AdminPhotoRoutes"));
const AdminManualReviewPhotos = lazy(() => import("./pages/AdminManualReviewPhotos"));
const AdminFaceSuggestionsQueue = lazy(() => import("./pages/AdminFaceSuggestionsQueue"));
const AdminFaceBackfill = lazy(() => import("./pages/AdminFaceBackfill"));
const AdminCaptionTagging = lazy(() => import("./pages/AdminCaptionTagging"));
const AdminPhotoGateTuning = lazy(() => import("./pages/AdminPhotoGateTuning"));
const AdminDuplicatePhotos = lazy(() => import("./pages/AdminDuplicatePhotos"));
const AdminUnlinkedUploads = lazy(() => import("./pages/AdminUnlinkedUploads"));
const AdminUploadsInbox = lazy(() => import("./pages/AdminUploadsInbox"));
const AdminLandRecordMatches = lazy(() => import("./pages/AdminLandRecordMatches"));
const AdminResearchPhotoId = lazy(() => import("./pages/AdminResearchPhotoId"));
const AdminPersonPhotoGallery = lazy(() => import("./pages/AdminPersonPhotoGallery"));
const AdminIdentityReview = lazy(() => import("./pages/AdminIdentityReview"));
const ArchiveIndex = lazy(() => import("./pages/ArchiveIndex"));
const ArchiveItem = lazy(() => import("./pages/ArchiveItem"));
const HcpoaMarch1981Notice = lazy(() => import("./pages/archive/HcpoaMarch1981Notice"));
const HcpoaSchreinerIIIEnvelope = lazy(() => import("./pages/archive/HcpoaSchreinerIIIEnvelope"));
const StaffArchiveLibrary = lazy(() => import("./pages/StaffArchiveLibrary"));
const AdminImageDescriptions = lazy(() => import("./pages/AdminImageDescriptions"));
const AdminFirearmTips = lazy(() => import("./pages/AdminFirearmTips"));
const AdminPhotoTips = lazy(() => import("./pages/AdminPhotoTips"));
const AdminGunRoomGallery = lazy(() => import("./pages/AdminGunRoomGallery"));
const AdminGalleries = lazy(() => import("./pages/AdminGalleries"));
const AdminGalleryEditor = lazy(() => import("./pages/AdminGalleryEditor"));
const AdminFacebookImport = lazy(() => import("./pages/AdminFacebookImport"));
const AdminGalleryImportAudit = lazy(() => import("./pages/AdminGalleryImportAudit"));
const AdminPhotoUpload = lazy(() => import("./pages/AdminPhotoUpload"));
const GalleryPage = lazy(() => import("./pages/GalleryPage"));
const AdminCaptainPortraits = lazy(() => import("./pages/AdminCaptainPortraits"));
const AdminSiblingPortraits = lazy(() => import("./pages/AdminSiblingPortraits"));
const AdminSiblingGalleryImages = lazy(() => import("./pages/AdminSiblingGalleryImages"));
const AdminBobSnowAlbum = lazy(() => import("./pages/AdminBobSnowAlbum"));
const AdminTagging = lazy(() => import("./pages/AdminTagging"));
const AdminBiographyEditor = lazy(() => import("./pages/AdminBiographyEditor"));
const AdminPhotoPeople = lazy(() => import("./pages/AdminPhotoPeople"));
const AdminPeopleRegistry = lazy(() => import("./pages/AdminPeopleRegistry"));
const AdminArtifactNotesEditor = lazy(() => import("./pages/AdminArtifactNotesEditor"));
const AdminArtifactGalleryImages = lazy(() => import("./pages/AdminArtifactGalleryImages"));
const AdminDeedReview = lazy(() => import("./pages/AdminDeedReview"));
const AdminDeedReviewDetail = lazy(() => import("./pages/AdminDeedReviewDetail"));
const AdminDeedIntegrity = lazy(() => import("./pages/AdminDeedIntegrity"));
const AdminScanTextIndex = lazy(() => import("./pages/AdminScanTextIndex"));
const AdminDeedAssistant = lazy(() => import("./pages/AdminDeedAssistant"));
const AdminHistoricDeedSplitter = lazy(() => import("./pages/AdminHistoricDeedSplitter"));
const AdminSecurityReport = lazy(() => import("./pages/AdminSecurityReport"));
const AdminContactSubmissions = lazy(() => import("./pages/AdminContactSubmissions"));
const AdminAppointments = lazy(() => import("./pages/AdminAppointments"));
const RequestAppointment = lazy(() => import("./pages/RequestAppointment"));
const AdminTestLogin = lazy(() => import("./pages/AdminTestLogin"));
const AdminResetPassword = lazy(() => import("./pages/AdminResetPassword"));
const CaptainCrowder = lazy(() => import("./pages/CaptainCrowder"));
const CaptainJackson = lazy(() => import("./pages/CaptainJackson"));
const CaptainGonzaullas = lazy(() => import("./pages/CaptainGonzaullas"));
const CaptainDean = lazy(() => import("./pages/CaptainDean"));
const RangerKrumnow = lazy(() => import("./pages/RangerKrumnow"));
const LieutenantOdom = lazy(() => import("./pages/LieutenantOdom"));
const CaptainGillett = lazy(() => import("./pages/CaptainGillett"));
const CaptainHamer = lazy(() => import("./pages/CaptainHamer"));
const CaptainHickman = lazy(() => import("./pages/CaptainHickman"));
const CaptainAllee = lazy(() => import("./pages/CaptainAllee"));
const CaptainFox = lazy(() => import("./pages/CaptainFox"));
const CaptainAldrich = lazy(() => import("./pages/CaptainAldrich"));
const CaptainCoffee = lazy(() => import("./pages/CaptainCoffee"));
const RepresentativeCanales = lazy(() => import("./pages/RepresentativeCanales"));
const CaptainKlevenhagen = lazy(() => import("./pages/CaptainKlevenhagen"));
const CaptainJamesRogers = lazy(() => import("./pages/CaptainJamesRogers"));
const CaptainMcCulloch = lazy(() => import("./pages/CaptainMcCulloch"));
const PrivatePuckett = lazy(() => import("./pages/PrivatePuckett"));
const RangerPressClippings = lazy(() => import("./pages/RangerPressClippings"));
const RangerClippingDetail = lazy(() => import("./pages/RangerClippingDetail"));
const FrontierLegends = lazy(() => import("./pages/FrontierLegends"));
const RangerClifton = lazy(() => import("./pages/RangerClifton"));
const CaptainHays = lazy(() => import("./pages/CaptainHays"));
const CaptainMcNelly = lazy(() => import("./pages/CaptainMcNelly"));
const CaptainHughes = lazy(() => import("./pages/CaptainHughes"));
const CaptainWalker = lazy(() => import("./pages/CaptainWalker"));
const SchreinerUniversity = lazy(() => import("./pages/SchreinerUniversity"));
const SchreinerProperties = lazy(() => import("./pages/SchreinerProperties"));
const SchreinerPropertiesMap = lazy(() => import("./pages/SchreinerPropertiesMap"));
const AlfredGiles = lazy(() => import("./pages/AlfredGiles"));
const SchreinerMercantile = lazy(() => import("./pages/SchreinerMercantile"));
const YOSocialClub = lazy(() => import("./pages/YOSocialClub"));
// FrontierJusticeExhibit merged into TexasRangerHall — route redirects
const RangerFirearmsEvolution = lazy(() => import("./pages/RangerFirearmsEvolution"));
const SchreinerIIICollection = lazy(() => import("./pages/SchreinerIIICollection"));
const SchreinerIIIFirearms = lazy(() => import("./pages/SchreinerIIIFirearms"));
const SchreinerIIIFirearmsRegistry = lazy(() => import("./pages/SchreinerIIIFirearmsRegistry"));
const SchreinerIIIBuybackList = lazy(() => import("./pages/SchreinerIIIBuybackList"));
const CharlieIIIColtAdLedger = lazy(() => import("./pages/CharlieIIIColtAdLedger"));
const SchreinerIIIAuction2003 = lazy(() => import("./pages/SchreinerIIIAuction2003"));
const SchreinerAuction2003CatalogPages = lazy(() => import("./pages/SchreinerAuction2003CatalogPages"));
const ArchiveSupplements2026 = lazy(() => import("./pages/ArchiveSupplements2026"));
const DocImportArchive = lazy(() => import("./pages/DocImportArchive"));
const DocImportReader = lazy(() => import("./pages/DocImportReader"));
const OcrSearch = lazy(() => import("./pages/OcrSearch"));
const SchreinerIIIAuctionCoverage = lazy(() => import("./pages/SchreinerIIIAuctionCoverage"));
const SchreinerIIITexasRangerArtifacts = lazy(() => import("./pages/SchreinerIIITexasRangerArtifacts"));
const RiaWantedLotsPage = lazy(() => import("./pages/RiaWantedLots"));
const RiaWantedLotsQrSheet = lazy(() => import("./pages/RiaWantedLotsQrSheet"));
const RiaWantedLotDetail = lazy(() => import("./pages/RiaWantedLotDetail"));
const MarshallSloverAcquisitions2026 = lazy(() => import("./pages/MarshallSloverAcquisitions2026"));
const MarshallSloverAcquisitionDetail = lazy(() => import("./pages/MarshallSloverAcquisitionDetail"));
const MarshallSloverSourceFigures = lazy(() => import("./pages/MarshallSloverSourceFigures"));
const OpenArchiveIndex = lazy(() => import("./pages/OpenArchiveIndex"));
const HeritageAcquisitionsImport = lazy(() => import("./pages/HeritageAcquisitionsImport"));
const HeritageAcquisitionsImportDetail = lazy(() => import("./pages/HeritageAcquisitionsImportDetail"));
const RangerOrganizationalHistory = lazy(() => import("./pages/RangerOrganizationalHistory"));
const RangerBadgeEvolution = lazy(() => import("./pages/RangerBadgeEvolution"));
const SchreinerPhilanthropy = lazy(() => import("./pages/SchreinerPhilanthropy"));
const SchreinerBankHeritage = lazy(() => import("./pages/SchreinerBankHeritage"));
const TLJYoRanchArticle = lazy(() => import("./pages/TLJYoRanchArticle"));
const YOOnceInAWhilePublication = lazy(() => import("./pages/YOOnceInAWhilePublication"));
const RangelandsYoRanchArticle = lazy(() => import("./pages/RangelandsYoRanchArticle"));
const ClaudeHeckShrader = lazy(() => import("./pages/ClaudeHeckShrader"));
const GunRoom = lazy(() => import("./pages/GunRoom"));
const PhotoComparison1920s = lazy(() => import("./pages/PhotoComparison1920s"));
const RustyCox = lazy(() => import("./pages/RustyCox"));
const VirgilRodgers = lazy(() => import("./pages/VirgilRodgers"));
const ArthurCate = lazy(() => import("./pages/ArthurCate"));
const GeorgeHey = lazy(() => import("./pages/GeorgeHey"));
const WarrenKlein = lazy(() => import("./pages/WarrenKlein"));
const JamesAGray = lazy(() => import("./pages/JamesAGray"));
const YORanchCentennial = lazy(() => import("./pages/YORanchCentennial"));
const YORanchHotelLoanCollection = lazy(() => import("./pages/YORanchHotelLoanCollection"));
const HistoricHillCountryStructures = lazy(() => import("./pages/HistoricHillCountryStructures"));
const ZachSager = lazy(() => import("./pages/ZachSager"));
const HalSwiggett = lazy(() => import("./pages/HalSwiggett"));
const CharlesSchreinerJr = lazy(() => import("./pages/CharlesSchreinerJr"));
const ImageDashboard = lazy(() => import("./pages/ImageDashboard"));
const CampVerdeCSA = lazy(() => import("./pages/CampVerdeCSA"));
const SchreinerBooklet = lazy(() => import("./pages/SchreinerBooklet"));
const BowlinSchreinerPamphlet = lazy(() => import("./pages/BowlinSchreinerPamphlet"));
const VonRosenbergLettersIndex = lazy(() => import("./pages/VonRosenbergLettersIndex"));
const VonRosenbergLetterDetail = lazy(() => import("./pages/VonRosenbergLetterDetail"));
const KlondikeLetter1898 = lazy(() => import("./pages/KlondikeLetter1898"));
const YOGuestBrochure1960s = lazy(() => import("./pages/YOGuestBrochure1960s"));
const HeadquartersMapThenNow = lazy(() => import("./pages/HeadquartersMapThenNow"));
const PhotoExhibit = lazy(() => import("./pages/PhotoExhibit"));
const PhotoGallery = lazy(() => import("./pages/PhotoGallery"));
const ArchivePhotosByDecade = lazy(() => import("./pages/ArchivePhotosByDecade"));
const RanchPhotographGallery = lazy(() => import("./pages/RanchPhotographGallery"));
const YORanchPhotographs = lazy(() => import("./pages/YORanchPhotographs"));
const ArtifactCatalogue = lazy(() => import("./pages/ArtifactCatalogue"));
const FaceGallery = lazy(() => import("./pages/FaceGallery"));
const ReferenceGallery = lazy(() => import("./pages/ReferenceGallery"));
const PersonIdentifications = lazy(() => import("./pages/PersonIdentifications"));
const IdentifiedPeople = lazy(() => import("./pages/IdentifiedPeople"));
const PersonTimeline = lazy(() => import("./pages/PersonTimeline"));
const FaceViewer = lazy(() => import("./pages/FaceViewer"));

const RangerPhotographTimeline = lazy(() => import("./pages/RangerPhotographTimeline"));
const SchreinerFamilyMember = lazy(() => import("./pages/SchreinerFamilyMember"));
const ShareYourStory = lazy(() => import("./pages/ShareYourStory"));
const AdminGuestStories = lazy(() => import("./pages/AdminGuestStories"));
const AdminPersonPortraits = lazy(() => import("./pages/AdminPersonPortraits"));
const RanchTimeline = lazy(() => import("./pages/RanchTimeline"));
const SchreinerAlamoPreservation = lazy(() => import("./pages/SchreinerAlamoPreservation"));
const JosephineParker = lazy(() => import("./pages/JosephineParker"));
const JulesBaker = lazy(() => import("./pages/JulesBaker"));
const DMichaelHughes = lazy(() => import("./pages/DMichaelHughes"));
const BrokenArrowRanch = lazy(() => import("./pages/BrokenArrowRanch"));
const KarlKinsel = lazy(() => import("./pages/KarlKinsel"));
const DavidRickenbacker = lazy(() => import("./pages/DavidRickenbacker"));
const OutdoorAwarenessPrograms = lazy(() => import("./pages/OutdoorAwarenessPrograms"));
const CalNewnam = lazy(() => import("./pages/CalNewnam"));
const JimIsleib = lazy(() => import("./pages/JimIsleib"));
const DianeIsleib = lazy(() => import("./pages/DianeIsleib"));
const JamesRiceOutdoorAwareness = lazy(() => import("./pages/JamesRiceOutdoorAwareness"));
const LarryThornton = lazy(() => import("./pages/LarryThornton"));
const JeffSlomka = lazy(() => import("./pages/JeffSlomka"));
const GayeSlomka = lazy(() => import("./pages/GayeSlomka"));
const RobertMaxson = lazy(() => import("./pages/RobertMaxson"));
const HenryRansom = lazy(() => import("./pages/HenryRansom"));
const DocumentSearch = lazy(() => import("./pages/DocumentSearch"));
const DocumentGallery = lazy(() => import("./pages/DocumentGallery"));
const SearchPage = lazy(() => import("./pages/SearchPage"));
const RanchFoundPhotographs = lazy(() => import("./pages/RanchFoundPhotographs"));
const SaddleRoomArchive = lazy(() => import("./pages/SaddleRoomArchive"));
const YODeedTracker = lazy(() => import("./pages/YODeedTracker"));
const YOTrackerDashboard = lazy(() => import("./pages/yo-tracker/Dashboard"));
const YOTrackerDeedDetail = lazy(() => import("./pages/yo-tracker/DeedDetail"));
const YOTrackerMap = lazy(() => import("./pages/yo-tracker/MapView"));
const YOTrackerMapsGallery = lazy(() => import("./pages/yo-tracker/MapsGallery"));
const YOTrackerMapDetail = lazy(() => import("./pages/yo-tracker/MapDetail"));
const YOTrackerTimeline = lazy(() => import("./pages/yo-tracker/Timeline"));
const YOTrackerPeople = lazy(() => import("./pages/yo-tracker/People"));
const YOTrackerTasks = lazy(() => import("./pages/yo-tracker/Tasks"));
const YOTrackerGaps = lazy(() => import("./pages/yo-tracker/Gaps"));
const YOTrackerResearchQueue = lazy(() => import("./pages/yo-tracker/ResearchQueue"));
const YOTrackerDocuments = lazy(() => import("./pages/yo-tracker/Documents"));
const YOTrackerResources = lazy(() => import("./pages/yo-tracker/Resources"));
const YOTrackerDuplicates = lazy(() => import("./pages/yo-tracker/Duplicates"));
const YOTrackerQADeedLinks = lazy(() => import("./pages/yo-tracker/QADeedLinks"));
const YOTrackerQADriveAudit = lazy(() => import("./pages/yo-tracker/QADriveAudit"));
const YOTrackerQADriveMirror = lazy(() => import("./pages/yo-tracker/QADriveMirror"));
const YOTrackerQADriveIngest = lazy(() => import("./pages/yo-tracker/QADriveIngest"));
const YOTrackerQATractExtract = lazy(() => import("./pages/yo-tracker/QATractExtract"));
const YOTrackerTractReview = lazy(() => import("./pages/yo-tracker/TractReview"));
const YOTrackerQALinkOrphans = lazy(() => import("./pages/yo-tracker/QALinkOrphans"));
const YOTrackerQAPageRanges = lazy(() => import("./pages/yo-tracker/QAPageRanges"));
const YOTrackerQASplitRename = lazy(() => import("./pages/yo-tracker/QASplitRename"));
const YOTrackerTracts = lazy(() => import("./pages/yo-tracker/Tracts"));
const YOTrackerUnmatchedTracts = lazy(() => import("./pages/yo-tracker/UnmatchedTracts"));
const PhotoDetail = lazy(() => import("./pages/PhotoDetail"));
const PeopleIndex = lazy(() => import("./pages/PeopleIndex"));
const HallOfFamePortraits = lazy(() => import("./pages/HallOfFamePortraits"));
const PortraitWall = lazy(() => import("./pages/PortraitWall"));
const TexasRangerHallOfFame = lazy(() => import("./pages/TexasRangerHallOfFame"));
const YORanch = lazy(() => import("./pages/YORanch"));
const TexasRangerHallOfFameTimeline = lazy(() => import("./pages/TexasRangerHallOfFameTimeline"));
const TexasRangersIndex = lazy(() => import("./pages/TexasRangersIndex"));
const RangerServiceEras = lazy(() => import("./pages/RangerServiceEras"));
const RangerServiceEra = lazy(() => import("./pages/RangerServiceEra"));
const TexasRangerEquipment = lazy(() => import("./pages/TexasRangerEquipment"));
const TexasRangerTimeline = lazy(() => import("./pages/TexasRangerTimeline"));
const TexasRangerGlossary = lazy(() => import("./pages/TexasRangerGlossary"));
const YORanchExhibits = lazy(() => import("./pages/YORanchExhibits"));
const SchreinerGenerations = lazy(() => import("./pages/SchreinerGenerations"));
const TexasRangerEquipmentCategory = lazy(() => import("./pages/TexasRangerEquipmentCategory"));
const PersonBioPage = lazy(() => import("./pages/PersonBioPage"));
const Explore = lazy(() => import("./pages/Explore"));
const SenatorHawley = lazy(() => import("./pages/SenatorHawley"));
const JohnsonFryCollection = lazy(() => import("./pages/JohnsonFryCollection"));
const BertieVerner = lazy(() => import("./pages/BertieVerner"));
const GarnetBrooks = lazy(() => import("./pages/GarnetBrooks"));
const AdminLinkAudit = lazy(() => import("./pages/AdminLinkAudit"));
const AdminGalleryCreditAudit = lazy(() => import("./pages/AdminGalleryCreditAudit"));
const AdminBrokenLinks = lazy(() => import("./pages/AdminBrokenLinks"));
const AdminNotFoundLog = lazy(() => import("./pages/AdminNotFoundLog"));
const AdminDuplicates = lazy(() => import("./pages/AdminDuplicates"));
const AdminAssetsCoverage = lazy(() => import("./pages/AdminAssetsCoverage"));
const AdminMergeLog = lazy(() => import("./pages/AdminMergeLog"));
const AdminArtifactDedupDebug = lazy(() => import("./pages/AdminArtifactDedupDebug"));
const AdminQrLabels = lazy(() => import("./pages/AdminQrLabels"));
const AdminAuctionFirearmLinks = lazy(() => import("./pages/AdminAuctionFirearmLinks"));
const AdminAuctionFirearmReview = lazy(() => import("./pages/AdminAuctionFirearmReview"));
const AdminLonghornMatchAudit = lazy(() => import("./pages/AdminLonghornMatchAudit"));
const AdminCollectionMatchAudit = lazy(() => import("./pages/AdminCollectionMatchAudit"));
const AdminFirearmExhibitLinks = lazy(() => import("./pages/AdminFirearmExhibitLinks"));
const AdminFalsePositives = lazy(() => import("./pages/AdminFalsePositives"));
const AdminReviewQueue = lazy(() => import("./pages/AdminReviewQueue"));
const AdminTagConflicts = lazy(() => import("./pages/AdminTagConflicts"));
const YORanchHeritage = lazy(() => import("./pages/YORanchHeritage"));
const HeritageCmsPage = lazy(() => import("./pages/HeritageCmsPage"));
const AdminHeritageEditor = lazy(() => import("./pages/AdminHeritageEditor"));
const AdminCmsEditor = lazy(() => import("./pages/AdminCmsEditor"));
const AdminOcrReview = lazy(() => import("./pages/AdminOcrReview"));
const AdminOcrJobDashboard = lazy(() => import("./pages/AdminOcrJobDashboard"));
const AdminOcrQualityAudit = lazy(() => import("./pages/AdminOcrQualityAudit"));
const AdminOcrRequeue = lazy(() => import("./pages/AdminOcrRequeue"));
const AdminOcrJobDetail = lazy(() => import("./pages/AdminOcrJobDetail"));
const DebugPlaceholders = lazy(() => import("./pages/DebugPlaceholders"));


const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onSuccess(data, query) {
      addSentryBreadcrumb({
        category: "api",
        type: "http",
        message: `Query success: ${JSON.stringify(query.queryKey)}`,
        data: { queryKey: query.queryKey, dataType: typeof data },
        level: "info",
      });
    },
    onError(error, query) {
      addSentryBreadcrumb({
        category: "api",
        type: "http",
        message: `Query error: ${JSON.stringify(query.queryKey)}`,
        data: { queryKey: query.queryKey, error: String(error) },
        level: "error",
      });
    },
  }),
  mutationCache: new MutationCache({
    onSuccess(data, variables, context, mutation) {
      addSentryBreadcrumb({
        category: "api",
        type: "http",
        message: `Mutation success: ${String(mutation.options.mutationKey ?? "unknown")}`,
        data: { mutationKey: mutation.options.mutationKey, variables },
        level: "info",
      });
    },
    onError(error, variables, context, mutation) {
      addSentryBreadcrumb({
        category: "api",
        type: "http",
        message: `Mutation error: ${String(mutation.options.mutationKey ?? "unknown")}`,
        data: { mutationKey: mutation.options.mutationKey, error: String(error), variables },
        level: "error",
      });
    },
  }),
});

// Layout-aware loading fallback. Renders the real header + footer shell
// plus a route-aware skeleton (form/grid/explore variants chosen from the
// pathname) so transitions never expose an empty viewport. Wrapped in
// FallbackTimer so we can verify prefetch effectiveness by logging how long
// the skeleton stayed visible.
const PageLoader = () => {
  const { pathname } = useLocation();
  return (
    <FallbackTimer>
      <RouteSkeleton pathname={pathname} />
    </FallbackTimer>
  );
};

import { useCollectionOverrides } from "@/hooks/useCollectionOverrides";
import { useArtifactTagOverrides } from "@/hooks/useArtifactTagOverrides";
import { usePersonTagOverrides } from "@/hooks/usePersonTagOverrides";

/**
 * Side-effect-only component: hydrates the in-memory collection override
 * cache used by getArtifactCollections so the public site reflects admin
 * corrections globally.
 */
const CollectionOverridesLoader = () => {
  useCollectionOverrides();
  return null;
};

/**
 * Side-effect-only loader: hydrates the in-memory tag/category override
 * caches (artifact_tag_overrides + person_tag_overrides) so public read
 * surfaces (search, detail pages, listings) reflect curator edits without
 * each component re-fetching.
 */
const TagOverridesLoader = () => {
  useArtifactTagOverrides();
  usePersonTagOverrides();
  return null;
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <AnalyticsTracker />
          <SentryRouteBreadcrumb />
          <CollectionOverridesLoader />
          <TagOverridesLoader />
          <AdminHomeButton />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
              <Route path="/" element={<Index />} />
              <Route path="/index" element={<Navigate to="/" replace />} />
              <Route path="/museum" element={<Museum />} />
              <Route
                path="/explore"
                element={
                  <Suspense fallback={<FallbackTimer><RouteSkeleton variant="explore" /></FallbackTimer>}>
                    <Explore />
                  </Suspense>
                }
              />
              <Route
                path="/exhibits"
                element={
                  <Suspense fallback={<FallbackTimer><RouteSkeleton variant="explore" /></FallbackTimer>}>
                    <Exhibits />
                  </Suspense>
                }
              />
              <Route
                path="/on-display"
                element={
                  <Suspense fallback={<FallbackTimer><RouteSkeleton variant="explore" /></FallbackTimer>}>
                    <OnDisplay />
                  </Suspense>
                }
              />
              <Route path="/exhibits/on-display" element={<Navigate to="/on-display" replace />} />
              <Route path="/exhibits/db/:slug" element={<CmsExhibitDetail />} />
              <Route path="/exhibits/frontier-justice" element={<Navigate to="/texas-ranger-hall" replace />} />
              <Route path="/exhibits/schreiner-iii-collection" element={<SchreinerIIICollection />} />
              <Route path="/exhibits/first-hunting-lease" element={<FirstHuntingLease />} />
              <Route path="/land-history" element={<LandHistory />} />
              <Route path="/land-history/archive" element={<TractArchive />} />
              <Route path="/land-history/deeds" element={<DeedArchive />} />
              <Route path="/land-history/:tractKey" element={<TractHistory />} />
              <Route path="/explore/first-hunting-lease" element={<FirstHuntingLease />} />
              <Route path="/exhibits/:slug" element={<ExhibitDetailDispatcher />} />
              <Route path="/exhibits/saddle-history" element={<SaddleHistory />} />
              <Route path="/exhibits/longhorn-preservation" element={<LonghornPreservation />} />
              <Route path="/texas-longhorn-heritage" element={<TexasLonghornHeritage />} />
              <Route path="/longhorn-preservation-pioneers" element={<LonghornPreservationPioneers />} />
              <Route path="/longhorn-gallery" element={<LonghornGallery />} />
              <Route path="/historical-gallery" element={<HistoricalGalleryPage />} />
              <Route path="/historical-figures" element={<HistoricalFiguresIndex />} />
              <Route path="/tlbaa-founders" element={<TLBAAFounders />} />
              <Route path="/tlbaa-founders/walter-riedel" element={<WalterRiedel />} />
              <Route path="/tlbaa-founders/travis-marks" element={<TravisMarks />} />
              <Route path="/tlbaa-founders/oswald-sauer" element={<OswaldSauer />} />
              <Route path="/tlbaa-founders/elvin-blevins" element={<ElvinBlevins />} />
              <Route path="/tlbaa-founders/arthur-bright" element={<ArthurBright />} />
              <Route path="/tlbaa-founders/walter-b-scott" element={<WalterBScott />} />
              <Route path="/tlbaa-growth-timeline" element={<TLBAAGrowthTimeline />} />
              <Route path="/claude-heck-shrader" element={<ClaudeHeckShrader />} />
              <Route path="/captain-schreiner" element={<CaptainSchreiner />} />
              <Route path="/schreiner-iii" element={<CharlesSchreinerIII />} />
              <Route path="/gun-room" element={<GunRoom />} />
              <Route path="/schreiner-iii-firearms" element={<SchreinerIIIFirearms />} />
              <Route path="/schreiner-iii-firearms/registry" element={<SchreinerIIIFirearmsRegistry />} />
              <Route path="/schreiner-iii-buyback-list" element={<SchreinerIIIBuybackList />} />
              <Route path="/charlie-iii-colt-ad-ledger" element={<CharlieIIIColtAdLedger />} />
              <Route path="/schreiner-iii-auction-2003" element={<SchreinerIIIAuction2003 />} />
              <Route path="/schreiner-iii-auction-2003/catalog-pages" element={<SchreinerAuction2003CatalogPages />} />
              <Route path="/heritage/archive-supplements-2026" element={<ArchiveSupplements2026 />} />
              <Route path="/archive/doc-import" element={<DocImportArchive />} />
              <Route path="/archive/ocr-search" element={<OcrSearch />} />
              <Route path="/archive/doc-import/:folder" element={<DocImportArchive />} />
              <Route path="/archive/doc-import/:folder/reader" element={<DocImportReader />} />
              <Route path="/open-archive-index" element={<OpenArchiveIndex />} />
              <Route path="/schreiner-iii-auction-2003/coverage" element={<SchreinerIIIAuctionCoverage />} />
              <Route path="/schreiner-iii-auction-2003/texas-rangers" element={<SchreinerIIITexasRangerArtifacts />} />
              <Route path="/ria-wanted-lots" element={<RiaWantedLotsPage />} />
              <Route path="/ria-wanted-lots/qr-sheet" element={<RiaWantedLotsQrSheet />} />
              <Route path="/ria-wanted-lots/:lotId" element={<RiaWantedLotDetail />} />
              <Route path="/marshall-slover-acquisitions-2026" element={<MarshallSloverAcquisitions2026 />} />
              <Route path="/marshall-slover-acquisitions-2026/source-figures" element={<MarshallSloverSourceFigures />} />
              <Route path="/marshall-slover-acquisitions-2026/:id" element={<MarshallSloverAcquisitionDetail />} />
              <Route path="/heritage-acquisitions-import" element={<HeritageAcquisitionsImport />} />
              <Route path="/heritage-acquisitions-import/:id" element={<HeritageAcquisitionsImportDetail />} />
              <Route path="/schreiner-dynasty" element={<SchreinerDynasty />} />
              <Route path="/schreiner-family-tree" element={<SchreinerFamilyTreePage />} />
              <Route path="/schreiner-family-photos" element={<SchreinerFamilyPhotos />} />
              <Route path="/vital-records" element={<VitalRecords />} />
              <Route path="/vital-records-wanted" element={<VitalRecordsWanted />} />
              <Route path="/yo-fiscal-1983" element={<YOFiscalYear1983 />} />
              <Route path="/myrtle-schreiner" element={<MyrtleSchreiner />} />
              <Route path="/walter-schreiner" element={<WalterSchreiner />} />
              <Route path="/louis-schreiner" element={<LouisSchreiner />} />
              <Route path="/ac-schreiner" element={<ACSchreiner />} />
              <Route path="/gus-schreiner" element={<GusSchreiner />} />
              <Route path="/1917-division" element={<The1917Division />} />
              <Route path="/charles-schreiner-jr" element={<CharlesSchreinerJr />} />
              <Route path="/charles-schreiner-iv" element={<CharlesSchreinerIV />} />
              <Route path="/walter-schreiner-iv" element={<WalterSchreinerIV />} />
              <Route path="/gus-schreiner-iv" element={<GusSchreinerFourthGen />} />
              <Route path="/louie-schreiner-ii" element={<LouieSchreinerII />} />
              <Route path="/schreiner-family-album" element={<SchreinerFamilyAlbum />} />
              <Route path="/audrey-schreiner" element={<AudreySchreiner />} />
              <Route path="/marion-vandenburgh" element={<MarionVanDenburgh />} />
              <Route path="/patricia-ann-lopez" element={<PatriciaAnnLopez />} />
              <Route path="/lena-schreiner" element={<LenaSchreiner />} />
              <Route path="/frances-schreiner" element={<FrancesSchreiner />} />
              <Route path="/mimi-schreiner" element={<MimiSchreiner />} />
              <Route path="/madelina-schreiner" element={<MadelinaSchreiner />} />
              <Route path="/josephine-parker" element={<JosephineParker />} />
              <Route path="/ranch-characters" element={<RanchCharacters />} />
              <Route path="/jesse-garza" element={<JesseGarza />} />
              <Route path="/button-forehand" element={<ButtonForehand />} />
              <Route path="/jerome-alexander" element={<JeromeAlexander />} />
              <Route path="/guides-and-legends" element={<GuidesAndLegends />} />
              <Route path="/charlie-seale" element={<CharlieSeale />} />
              <Route path="/jimmy-peril" element={<JimmyPeril />} />
              <Route path="/bob-jay-darragh" element={<BobJayDarragh />} />
              <Route path="/david-nesbitt" element={<DavidNesbitt />} />
              <Route path="/louie-mecey" element={<LouieMecey />} />
              <Route path="/clyde-fischer" element={<ClydeFischer />} />
              <Route path="/jack-potter" element={<JackPotter />} />
              <Route path="/harvey-goff" element={<HarveyGoff />} />
              <Route path="/red-alexander" element={<RedAlexander />} />
              <Route path="/photo-search" element={<PhotoSearch />} />
              <Route path="/document-search" element={<DocumentSearch />} />
              <Route path="/document-gallery" element={<DocumentGallery />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/ranch-found-photographs" element={<RanchFoundPhotographs />} />
              <Route path="/saddle-room-archive" element={<SaddleRoomArchive />} />
              <Route path="/yo-historic-land-documents" element={<YOTrackerDashboard />} />
              <Route path="/yo-historic-land-documents/registry" element={<YODeedTracker />} />
              <Route path="/yo-historic-land-documents/deeds/:slug" element={<YOTrackerDeedDetail />} />
              <Route path="/yo-historic-land-documents/map" element={<YOTrackerMap />} />
              <Route path="/yo-historic-land-documents/maps" element={<YOTrackerMapsGallery />} />
              <Route path="/yo-historic-land-documents/maps/:slug" element={<YOTrackerMapDetail />} />
              <Route path="/yo-historic-land-documents/timeline" element={<YOTrackerTimeline />} />
              <Route path="/yo-historic-land-documents/people" element={<YOTrackerPeople />} />
              <Route path="/yo-historic-land-documents/people/:slug" element={<YOTrackerPeople />} />
              <Route path="/yo-historic-land-documents/tasks" element={<YOTrackerTasks />} />
              <Route path="/yo-historic-land-documents/gaps" element={<YOTrackerGaps />} />
              <Route path="/yo-historic-land-documents/research-queue" element={<YOTrackerResearchQueue />} />
              <Route path="/yo-historic-land-documents/documents" element={<YOTrackerDocuments />} />
              <Route path="/yo-historic-land-documents/resources" element={<YOTrackerResources />} />
              <Route path="/yo-historic-land-documents/duplicates" element={<YOTrackerDuplicates />} />
             <Route path="/yo-historic-land-documents/qa/deed-links" element={<YOTrackerQADeedLinks />} />
             <Route path="/yo-historic-land-documents/qa/drive-audit" element={<YOTrackerQADriveAudit />} />
            <Route path="/yo-historic-land-documents/qa/drive-mirror" element={<YOTrackerQADriveMirror />} />
             <Route path="/yo-historic-land-documents/qa/drive-ingest" element={<YOTrackerQADriveIngest />} />
             <Route path="/yo-historic-land-documents/qa/tract-extract" element={<AdminAuth><YOTrackerQATractExtract /></AdminAuth>} />
            <Route path="/yo-historic-land-documents/qa/link-orphans" element={<YOTrackerQALinkOrphans />} />
            <Route path="/yo-historic-land-documents/qa/page-ranges" element={<YOTrackerQAPageRanges />} />
            <Route path="/yo-historic-land-documents/qa/split-rename" element={<YOTrackerQASplitRename />} />
            <Route path="/yo-historic-land-documents/tracts" element={<YOTrackerTracts />} />
              <Route path="/yo-historic-land-documents/tracts/unmatched" element={<AdminAuth><YOTrackerUnmatchedTracts /></AdminAuth>} />
              <Route path="/yo-historic-land-documents/tracts/review" element={<AdminAuth><YOTrackerTractReview /></AdminAuth>} />
              {/* Legacy redirects: /yo-deed-tracker/* → /yo-historic-land-documents/* */}
              <Route path="/yo-deed-tracker" element={<Navigate to="/yo-historic-land-documents" replace />} />
              <Route path="/yo-deed-tracker/*" element={<LegacyDeedTrackerRedirect />} />
              <Route path="/photos/:slug" element={<PhotoDetail />} />
              <Route path="/people" element={<PeopleIndex />} />
              <Route path="/yo-ranch" element={<YORanch />} />
              <Route path="/hall-of-fame-portraits" element={<HallOfFamePortraits />} />
              <Route path="/portrait-wall" element={<PortraitWall />} />
              <Route path="/texas-ranger-hall-of-fame" element={<TexasRangerHallOfFame />} />
              <Route path="/texas-ranger-hall-of-fame-timeline" element={<TexasRangerHallOfFameTimeline />} />
              <Route path="/texas-rangers-index" element={<TexasRangersIndex />} />
              <Route path="/texas-ranger-eras" element={<RangerServiceEras />} />
              <Route path="/texas-ranger-eras/:decade" element={<RangerServiceEra />} />
              <Route path="/texas-ranger-equipment" element={<TexasRangerEquipment />} />
              <Route path="/texas-ranger-equipment/:category" element={<TexasRangerEquipmentCategory />} />
              <Route path="/texas-ranger-timeline" element={<TexasRangerTimeline />} />
              <Route path="/texas-ranger-glossary" element={<TexasRangerGlossary />} />
              <Route path="/yo-ranch-exhibits" element={<YORanchExhibits />} />
              <Route path="/schreiner-generations" element={<SchreinerGenerations />} />
              <Route path="/people/:slug" element={<PersonBioPage />} />
              <Route path="/content-search" element={<Navigate to="/document-search" replace />} />
              <Route path="/clarence-hyde" element={<ClarenceHyde />} />
              <Route path="/captain-bob-snow" element={<CaptainBobSnow />} />
              <Route path="/captain-bob-snow/photo/:entryId/related" element={<BobSnowPhotoRelated />} />
              <Route path="/adam-carter" element={<AdamCarter />} />
              <Route path="/garland-scogin" element={<GarlandScogin />} />
              <Route path="/mac-hyde" element={<MacHyde />} />
              <Route path="/garza-family" element={<GarzaFamily />} />
              <Route path="/dr-russell-thomas" element={<DrRussellThomas />} />
              <Route path="/virgil-rodgers" element={<VirgilRodgers />} />
              <Route path="/arthur-cate" element={<ArthurCate />} />
              <Route path="/george-hey" element={<GeorgeHey />} />
              <Route path="/warren-klein" element={<WarrenKlein />} />
              <Route path="/james-a-gray" element={<JamesAGray />} />
              <Route path="/zach-sager" element={<ZachSager />} />
              <Route path="/bertie-verner" element={<BertieVerner />} />
              <Route path="/hal-swiggett" element={<HalSwiggett />} />
              <Route path="/exotic-species" element={<ExoticSpecies />} />
              <Route path="/exotic-species/giraffe" element={<GiraffeSpecies />} />
              <Route path="/exotic-species/yo-ibex" element={<YOIbexSpecies />} />
              <Route path="/exotic-species/:id" element={<ExoticSpecies />} />
              {/* Four legacy exotic pages — now CMS-backed via heritage_pages slugs.
                  Routes and redirects intentionally unchanged; only the rendered
                  component swaps to HeritagePageRenderer. */}
              <Route path="/wildlife-heritage" element={<WildlifeHeritage />} />
              <Route path="/exotic-wildlife" element={<HeritageRoute slug="exotic-wildlife" />} />
              <Route path="/exotic-wildlife-program" element={<HeritageRoute slug="exotic-wildlife-program" />} />
              <Route path="/exotic-wildlife-association" element={<HeritageRoute slug="exotic-wildlife-association" />} />
              {/* Legacy/alias redirects → Wildlife hub */}
              <Route path="/wildlife" element={<Navigate to="/wildlife-heritage" replace />} />
              <Route path="/exotics" element={<Navigate to="/exotic-species" replace />} />
              <Route path="/exotic-game" element={<Navigate to="/exotic-species" replace />} />
              <Route path="/exotic-animals" element={<Navigate to="/exotic-species" replace />} />
              <Route path="/exotic-hunting" element={<Navigate to="/exotic-species" replace />} />
              <Route path="/wildlife-program" element={<Navigate to="/exotic-wildlife-program" replace />} />
              <Route path="/exotic-program" element={<Navigate to="/exotic-wildlife-program" replace />} />
              <Route path="/ewa" element={<Navigate to="/exotic-wildlife-association" replace />} />
              <Route path="/exotic-wildlife-heritage" element={<Navigate to="/wildlife-heritage" replace />} />
              <Route path="/conservation" element={<Navigate to="/wildlife-heritage" replace />} />
              {/* Catch-all for any unknown URL under the exotic / wildlife
                  namespaces. Matches AFTER all specific routes/redirects above
                  so it only fires for stale legacy paths, and BEFORE the
                  global 404 catch-all at the bottom of the router. */}
              <Route path="/exotic-wildlife/*" element={<LegacyExoticRedirect />} />
              <Route path="/exotic-wildlife-program/*" element={<LegacyExoticRedirect />} />
              <Route path="/exotic-wildlife-association/*" element={<LegacyExoticRedirect />} />
              <Route path="/wildlife-heritage/*" element={<LegacyExoticRedirect />} />
              <Route path="/exotics/*" element={<LegacyExoticRedirect />} />
              <Route path="/wildlife/*" element={<LegacyExoticRedirect />} />
              <Route path="/exotic/*" element={<LegacyExoticRedirect />} />
              <Route path="/exotic-game/*" element={<LegacyExoticRedirect />} />
              <Route path="/exotic-animals/*" element={<LegacyExoticRedirect />} />
              <Route path="/exotic-hunting/*" element={<LegacyExoticRedirect />} />
              <Route path="/exotic-program/*" element={<LegacyExoticRedirect />} />
              <Route path="/wildlife-program/*" element={<LegacyExoticRedirect />} />
              <Route path="/ewa/*" element={<LegacyExoticRedirect />} />
              <Route path="/d-michael-hughes" element={<DMichaelHughes />} />
              <Route path="/broken-arrow-ranch" element={<BrokenArrowRanch />} />
              <Route path="/karl-kinsel" element={<KarlKinsel />} />
              <Route path="/david-rickenbacker" element={<DavidRickenbacker />} />
              <Route path="/outdoor-awareness-programs" element={<OutdoorAwarenessPrograms />} />
              <Route path="/cal-newnam" element={<CalNewnam />} />
              <Route path="/jim-isleib" element={<JimIsleib />} />
              <Route path="/diane-isleib" element={<DianeIsleib />} />
              <Route path="/james-rice-outdoor-awareness" element={<JamesRiceOutdoorAwareness />} />
              <Route path="/larry-thornton" element={<LarryThornton />} />
              <Route path="/jeff-slomka" element={<JeffSlomka />} />
              <Route path="/gaye-slomka" element={<GayeSlomka />} />
              <Route path="/robert-maxson" element={<RobertMaxson />} />
              <Route path="/garnet-brooks" element={<GarnetBrooks />} />
              <Route path="/wildlife-safari" element={<WildlifeSafari />} />
              <Route path="/hunting-heritage" element={<HuntingHeritage />} />
              <Route path="/trophy-records-hunters" element={<TrophyRecordsHunters />} />
              <Route path="/famous-visitors" element={<FamousVisitors />} />
              <Route path="/visitor-timeline" element={<VisitorTimeline />} />
              <Route path="/visitor-face-timeline" element={<VisitorFaceTimeline />} />
              <Route path="/visitor-gallery" element={<VisitorGallery />} />
              <Route path="/visitor-map" element={<VisitorMap />} />
              <Route path="/visitor-directory" element={<VisitorDirectory />} />
              <Route path="/visitors/:slug" element={<VisitorBiography />} />
              <Route path="/visitors/:slug/timeline" element={<VisitorPersonTimeline />} />
              <Route path="/visitor-exhibits" element={<VisitorExhibits />} />
              <Route path="/astronaut-room" element={<AstronautRoom />} />
              <Route path="/joe-engle" element={<JoeEngle />} />
              <Route path="/chuck-yeager" element={<ChuckYeager />} />
              <Route path="/doug-walker" element={<DougWalker />} />
              <Route path="/ron-schara" element={<RonSchara />} />
              <Route path="/norman-schwarzkopf" element={<NormanSchwarzkopf />} />
              <Route path="/lyndon-johnson" element={<LyndonJohnson />} />
              <Route path="/john-connally" element={<JohnConnally />} />
              <Route path="/dolph-briscoe" element={<DolphBriscoe />} />
              <Route path="/ace-reid" element={<AceReid />} />
              <Route path="/joe-beeler" element={<JoeBeeler />} />
              <Route path="/fred-gipson" element={<FredGipson />} />
              <Route path="/hondo-crouch" element={<HondoCrouch />} />
              <Route path="/hollywood-media" element={<HollywoodMedia />} />
              <Route path="/news-media" element={<NewsMedia />} />
              <Route path="/literary-legacy" element={<LiteraryLegacy />} />
              <Route path="/j-frank-dobie" element={<JFrankDobie />} />
              <Route path="/james-michener" element={<JamesMichener />} />
              <Route path="/elliott-means" element={<ElliottMeans />} />
              <Route path="/michael-wayne" element={<MichaelWayne />} />
              <Route path="/patrick-wayne" element={<PatrickWayne />} />
              <Route path="/audie-murphy" element={<AudieMurphy />} />
              <Route path="/thomas-haden-church" element={<ThomasHadenChurch />} />
              <Route path="/literary-legacy/tlj-yo-ranch" element={<TLJYoRanchArticle />} />
              <Route path="/literary-legacy/yo-once-in-a-while" element={<YOOnceInAWhilePublication />} />
              <Route path="/literary-legacy/rangelands-yo-ranch-1984" element={<RangelandsYoRanchArticle />} />
              <Route path="/culture-and-fame" element={<CultureAndFame />} />
              <Route path="/yo-sam-houston" element={<YOSamHouston />} />
              <Route path="/yo-carmela" element={<YOCarmela />} />
              <Route path="/bevo-vii" element={<BevoVII />} />
              <Route path="/heritage" element={<Heritage />} />
              <Route path="/yo-ranch-heritage" element={<YORanchHeritage />} />
              <Route path="/heritage/schreiner-dynasty" element={<Navigate to="/schreiner-dynasty" replace />} />
              <Route path="/heritage/captain-schreiner" element={<Navigate to="/captain-schreiner" replace />} />
              <Route path="/heritage/charles-schreiner-iii" element={<Navigate to="/schreiner-iii" replace />} />
              <Route path="/heritage/ranch-history" element={<Navigate to="/ranch-history" replace />} />
              <Route path="/heritage/the-museum" element={<Navigate to="/museum" replace />} />
              <Route path="/texas-longhorns" element={<Navigate to="/texas-longhorn-heritage" replace />} />
              {/* Generic CMS-driven heritage/exotic page renderer. Use a
                  `heritage_pages` slug here (e.g. /heritage/exotic-species)
                  to render the page entirely from CMS data. */}
              <Route path="/heritage/:slug" element={<HeritageCmsPage />} />
              <Route path="/stewardship" element={<Stewardship />} />
              <Route
                path="/collections"
                element={
                  <Suspense fallback={<FallbackTimer><RouteSkeleton variant="explore" /></FallbackTimer>}>
                    <Collections />
                  </Suspense>
                }
              />
              <Route path="/collections/historic-firearms" element={<Navigate to="/collections/firearms" replace />} />
              <Route path="/collections/firearms" element={<FirearmsCollection />} />
              <Route path="/collections/firearms-evolution" element={<RangerFirearmsEvolution />} />
              <Route path="/colt-model-1878-scholarship" element={<ColtModel1878Scholarship />} />
              <Route path="/aten-brothers" element={<AtenBrothers />} />
              <Route path="/h-clay-dahlberg" element={<HClayDahlberg />} />
              <Route path="/collections/:type" element={<CollectionDetail />} />
              <Route path="/texas-rangers" element={<TexasRangers />} />
              <Route path="/native-american-heritage" element={<NativeAmericanHeritage />} />
              <Route path="/texas-rangers/crowder" element={<CaptainCrowder />} />
              <Route path="/texas-rangers/jackson" element={<CaptainJackson />} />
              <Route path="/texas-rangers/gonzaullas" element={<CaptainGonzaullas />} />
              <Route path="/texas-rangers/dean" element={<CaptainDean />} />
              <Route path="/texas-rangers/krumnow" element={<RangerKrumnow />} />
              <Route path="/texas-rangers/odom" element={<LieutenantOdom />} />
              <Route path="/texas-rangers/gillett" element={<CaptainGillett />} />
              <Route path="/texas-rangers/hamer" element={<CaptainHamer />} />
              <Route path="/texas-rangers/hickman" element={<CaptainHickman />} />
              <Route path="/texas-rangers/allee" element={<CaptainAllee />} />
              <Route path="/captain-hamer" element={<CaptainHamer />} />
              <Route path="/captain-hickman" element={<CaptainHickman />} />
              <Route path="/captain-allee" element={<CaptainAllee />} />
              <Route path="/captain-gonzaullas" element={<CaptainGonzaullas />} />
              <Route path="/captain-jackson" element={<CaptainJackson />} />
              <Route path="/captain-crowder" element={<CaptainCrowder />} />
              <Route path="/captain-dean" element={<CaptainDean />} />
              <Route path="/captain-gillett" element={<CaptainGillett />} />
              <Route path="/captain-fox" element={<CaptainFox />} />
              <Route path="/captain-aldrich" element={<CaptainAldrich />} />
              <Route path="/captain-coffee" element={<CaptainCoffee />} />
              <Route path="/captain-klevenhagen" element={<CaptainKlevenhagen />} />
              <Route path="/texas-rangers/klevenhagen" element={<CaptainKlevenhagen />} />
              <Route path="/captain-james-rogers" element={<CaptainJamesRogers />} />
              <Route path="/texas-rangers/james-rogers" element={<CaptainJamesRogers />} />
              <Route path="/captain-mcculloch" element={<CaptainMcCulloch />} />
              <Route path="/texas-rangers/mcculloch" element={<CaptainMcCulloch />} />
              <Route path="/texas-rangers/fox" element={<CaptainFox />} />
              <Route path="/texas-rangers/aldrich" element={<CaptainAldrich />} />
              <Route path="/texas-rangers/coffee" element={<CaptainCoffee />} />
              <Route path="/texas-rangers/canales" element={<RepresentativeCanales />} />
              <Route path="/texas-rangers/puckett" element={<PrivatePuckett />} />
              <Route path="/ranger-clifton" element={<RangerClifton />} />
              <Route path="/texas-rangers/clifton" element={<RangerClifton />} />
              <Route path="/ranger-press-clippings" element={<RangerPressClippings />} />
              <Route path="/ranger-press-clippings/:slug" element={<RangerClippingDetail />} />
              <Route path="/texas-ranger-hall" element={<TexasRangerHall />} />
              <Route path="/ranger-organizational-history" element={<RangerOrganizationalHistory />} />
              <Route path="/jules-baker" element={<JulesBaker />} />
              <Route path="/henry-ransom" element={<HenryRansom />} />
              <Route path="/senator-hawley" element={<SenatorHawley />} />
              <Route path="/johnson-fry-collection" element={<JohnsonFryCollection />} />
              <Route path="/ranger-badge-evolution" element={<RangerBadgeEvolution />} />
              <Route path="/frontier-legends" element={<FrontierLegends />} />
              <Route path="/texas-rangers/hays" element={<CaptainHays />} />
              <Route path="/texas-rangers/mcnelly" element={<CaptainMcNelly />} />
              <Route path="/texas-rangers/hughes" element={<CaptainHughes />} />
              <Route path="/texas-rangers/walker" element={<CaptainWalker />} />
              <Route path="/ranch-history" element={<RanchHistory />} />
              <Route path="/ranch-landmarks" element={<RanchLandmarks />} />
              <Route path="/camp-verde-csa" element={<CampVerdeCSA />} />
              <Route path="/schreiner-booklet" element={<SchreinerBooklet />} />
              <Route path="/schreiner-bowlin-pamphlet" element={<BowlinSchreinerPamphlet />} />
              <Route path="/land-records-surveys" element={<Navigate to="/yo-historic-land-documents" replace />} />
              <Route path="/von-rosenberg-letters" element={<VonRosenbergLettersIndex />} />
              <Route path="/von-rosenberg-letters/:slug" element={<VonRosenbergLetterDetail />} />
              <Route path="/klondike-letter-1898" element={<KlondikeLetter1898 />} />
              <Route path="/yo-guest-brochure-1960s" element={<YOGuestBrochure1960s />} />
              <Route path="/headquarters-map-then-and-now" element={<HeadquartersMapThenNow />} />
              <Route path="/photo-exhibit" element={<PhotoExhibit />} />
              <Route path="/photo-gallery" element={<PhotoGallery />} />
              <Route path="/archive-photographs" element={<ArchivePhotosByDecade />} />
              <Route path="/ranch-photograph-gallery" element={<RanchPhotographGallery />} />
              <Route path="/yo-ranch-photographs" element={<YORanchPhotographs />} />
              <Route
                path="/ranger-photograph-timeline"
                element={<RangerPhotographTimeline />}
              />
              <Route path="/schreiner-family/:slug" element={<SchreinerFamilyMember />} />
              <Route path="/share-your-story" element={<ShareYourStory />} />
              <Route path="/ranch-timeline" element={<RanchTimeline />} />
              <Route path="/schreiner-university" element={<SchreinerUniversity />} />
              <Route path="/schreiner-properties" element={<SchreinerProperties />} />
              <Route path="/schreiner-properties-map" element={<SchreinerPropertiesMap />} />
              <Route path="/alfred-giles" element={<AlfredGiles />} />
              <Route path="/historic-hill-country-structures" element={<HistoricHillCountryStructures />} />
              <Route path="/schreiner-mercantile" element={<SchreinerMercantile />} />
              <Route path="/schreiner-philanthropy" element={<SchreinerPhilanthropy />} />
              <Route path="/schreiner-alamo-preservation" element={<SchreinerAlamoPreservation />} />
              <Route path="/schreiner-bank-heritage" element={<SchreinerBankHeritage />} />
              <Route path="/yo-social-club" element={<YOSocialClub />} />
              <Route path="/photo-comparison-1920s" element={<PhotoComparison1920s />} />
              <Route path="/yo-ranch-centennial" element={<YORanchCentennial />} />
              <Route path="/yo-ranch-hotel-loan-collection" element={<YORanchHotelLoanCollection />} />
              <Route path="/rusty-cox" element={<RustyCox />} />
              <Route path="/ranch-map" element={<RanchMap />} />
              <Route path="/visit" element={<Visit />} />
              <Route
                path="/visit/status"
                element={
                  <Suspense fallback={<FallbackTimer><RouteSkeleton variant="form" /></FallbackTimer>}>
                    <VisitStatus />
                  </Suspense>
                }
              />
              <Route
                path="/contact"
                element={
                  <Suspense fallback={<FallbackTimer><RouteSkeleton variant="form" /></FallbackTimer>}>
                    <Contact />
                  </Suspense>
                }
              />
              <Route
                path="/request-appointment"
                element={
                  <Suspense fallback={<FallbackTimer><RouteSkeleton variant="form" /></FallbackTimer>}>
                    <RequestAppointment />
                  </Suspense>
                }
              />
              <Route
                path="/artifacts/texas-rangers-dps-pamphlet/detail"
                element={<TexasDpsPamphletDetail />}
              />
              <Route path="/artifacts/:slug" element={<ArtifactDetail />} />
              <Route path="/artifacts" element={<ArtifactCatalogue />} />
              <Route path="/face-gallery" element={<FaceGallery />} />
              <Route path="/reference-gallery" element={<ReferenceGallery />} />
              <Route path="/identified-people" element={<IdentifiedPeople />} />
              <Route path="/person-timeline/:slug" element={<PersonTimeline />} />
              <Route path="/person-identifications/:slug" element={<PersonIdentifications />} />
              <Route path="/face-gallery/:faceId" element={<FaceViewer />} />

              <Route path="/artifact/:id" element={<ArtifactByIdRedirect />} />
              <Route path="/admin/exclusions" element={<AdminExclusions />} />
              <Route path="/admin/doc-import-triage" element={<AdminDocImportTriage />} />
              <Route path="/admin/doc-page-links" element={<AdminDocPageLinks />} />
              <Route path="/admin/doc-import-duplicates" element={<AdminDocImportDuplicates />} />
              <Route path="/admin/sideways-scans" element={<AdminSidewaysScans />} />
              <Route path="/admin/setup" element={<AdminSetup />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/guests" element={<AdminGuests />} />
              <Route path="/admin/deed-review" element={<AdminDeedReview />} />
              <Route path="/admin/deed-review/:slug" element={<AdminDeedReviewDetail />} />
              <Route path="/admin/deed-integrity" element={<AdminDeedIntegrity />} />
              <Route path="/admin/scan-text-index" element={<AdminScanTextIndex />} />
              <Route path="/admin/deed-assistant" element={<AdminDeedAssistant />} />
              <Route path="/admin/historic-deed-splitter" element={<AdminAuth><AdminHistoricDeedSplitter /></AdminAuth>} />
              <Route path="/admin/login" element={<AdminAuth><Navigate to="/admin" replace /></AdminAuth>} />
              <Route path="/debug-placeholders" element={<DebugPlaceholders />} />
              <Route path="/admin/test-login" element={<AdminTestLogin />} />
              <Route path="/admin/reset-password" element={<AdminResetPassword />} />
              <Route path="/admin/content-indexing" element={<AdminContentIndexing />} />
              <Route path="/admin/analyze-images" element={<AdminAnalyzeImages />} />
              <Route path="/admin/archive-review-queue" element={<AdminArchiveReviewQueue />} />
              <Route path="/admin/archive-to-cms" element={<AdminArchiveToCms />} />
              <Route path="/admin/archive" element={<AdminArchive />} />
              <Route path="/admin/archive/pages-backfill" element={<AdminArchivePagesBackfill />} />
              <Route path="/admin/historic-photos" element={<AdminHistoricPhotos />} />
              <Route path="/admin/photo-review" element={<AdminPhotoReview />} />
              <Route path="/admin/photo-research" element={<AdminPhotoResearchDashboard />} />
              <Route path="/admin/photo-archive" element={<AdminPhotoArchive />} />
              <Route path="/admin/photo-archive/:photoId" element={<AdminPhotoArchiveViewer />} />
              <Route path="/admin/photo-archive/review-queue" element={<AdminPhotoReviewQueue />} />
              <Route path="/admin/photo-archive/clusters" element={<AdminPhotoClusters />} />
              <Route path="/admin/photo-archive/clusters/:clusterId" element={<AdminPhotoClusterDetail />} />
              <Route path="/admin/photo-archive/alert-log" element={<AdminFaceAlertLog />} />
              <Route path="/admin/photo-identity" element={<AdminPhotoIdentityQueue />} />
              <Route path="/admin/identity-review" element={<AdminIdentityReview />} />
              <Route path="/admin/arcface-review" element={<AdminArcFaceReview />} />
              <Route path="/admin/face-name-conflicts" element={<AdminFaceNameConflicts />} />
              <Route path="/admin/visitor-biographies" element={<AdminVisitorBiographies />} />
              <Route path="/admin/visitor-vitals" element={<AdminVisitorVitals />} />
              <Route path="/admin/face-work-queue" element={<AdminFaceWorkQueue />} />
              <Route path="/admin/photo-work-queue" element={<AdminPhotoWorkQueue />} />
              <Route path="/admin/photo-identity/person/:slug" element={<AdminPersonPhotoGallery />} />
              <Route path="/admin/photo-routes" element={<AdminPhotoRoutes />} />
              <Route path="/admin/manual-review-photos" element={<AdminManualReviewPhotos />} />
              <Route path="/admin/face-suggestions" element={<AdminFaceSuggestionsQueue />} />
              <Route path="/admin/face-backfill" element={<AdminFaceBackfill />} />
              <Route path="/admin/caption-tagging" element={<AdminCaptionTagging />} />
              <Route path="/admin/photo-gate-tuning" element={<AdminPhotoGateTuning />} />
              <Route path="/admin/duplicate-photos" element={<AdminAuth><AdminDuplicatePhotos /></AdminAuth>} />
              <Route path="/admin/unlinked-uploads" element={<AdminAuth><AdminUnlinkedUploads /></AdminAuth>} />
              <Route path="/admin/uploads-inbox" element={<AdminUploadsInbox />} />
              <Route path="/admin/land-record-matches" element={<AdminLandRecordMatches />} />
              <Route path="/admin/research-photo-id" element={<AdminAuth><AdminResearchPhotoId /></AdminAuth>} />
              <Route path="/archive" element={<ArchiveIndex />} />
              <Route path="/archive/1981-hill-country-peace-officers-march-meeting" element={<HcpoaMarch1981Notice />} />
              <Route path="/archive/1981-hcpoa-envelope-schreiner-iii" element={<HcpoaSchreinerIIIEnvelope />} />
              <Route path="/archive/:slug" element={<ArchiveItem />} />
              <Route path="/staff/archive" element={<StaffArchiveLibrary />} />
              <Route path="/admin/firearm-tips" element={<AdminAuth><AdminFirearmTips /></AdminAuth>} />
              <Route path="/admin/photo-tips" element={<AdminAuth><AdminPhotoTips /></AdminAuth>} />
              <Route path="/admin/guest-stories" element={<AdminAuth><AdminGuestStories /></AdminAuth>} />
              <Route path="/admin/person-portraits" element={<AdminAuth><AdminPersonPortraits /></AdminAuth>} />
              <Route path="/admin/gun-room-gallery" element={<AdminAuth><AdminGunRoomGallery /></AdminAuth>} />
              <Route path="/admin/galleries" element={<AdminAuth><AdminGalleries /></AdminAuth>} />
              <Route path="/admin/galleries/:id" element={<AdminAuth><AdminGalleryEditor /></AdminAuth>} />
              <Route path="/galleries/:slug" element={<GalleryPage />} />
              <Route path="/admin/captain-portraits" element={<AdminAuth><AdminCaptainPortraits /></AdminAuth>} />
              <Route path="/admin/sibling-portraits" element={<AdminAuth><AdminSiblingPortraits /></AdminAuth>} />
              <Route path="/admin/sibling-gallery-images" element={<AdminAuth><AdminSiblingGalleryImages /></AdminAuth>} />
              <Route path="/admin/bob-snow-album" element={<AdminAuth><AdminBobSnowAlbum /></AdminAuth>} />
              <Route path="/admin/image-dashboard" element={<AdminAuth><ImageDashboard /></AdminAuth>} />
              <Route path="/admin/tagging" element={<AdminAuth><AdminTagging /></AdminAuth>} />
              <Route path="/admin/biographies" element={<AdminAuth><AdminBiographyEditor /></AdminAuth>} />
              <Route path="/admin/photo-people" element={<AdminAuth><AdminPhotoPeople /></AdminAuth>} />
              <Route path="/admin/people-registry" element={<AdminAuth><AdminPeopleRegistry /></AdminAuth>} />
              <Route path="/admin/artifact-notes" element={<AdminAuth><AdminArtifactNotesEditor /></AdminAuth>} />
              <Route path="/admin/artifact-gallery-images" element={<AdminAuth><AdminArtifactGalleryImages /></AdminAuth>} />
              <Route path="/admin/image-descriptions" element={<AdminImageDescriptions />} />
              <Route path="/admin/security-report" element={<AdminAuth><AdminSecurityReport /></AdminAuth>} />
              <Route path="/admin/contact-submissions" element={<AdminContactSubmissions />} />
              <Route path="/admin/appointments" element={<AdminAppointments />} />
              <Route path="/admin/link-audit" element={<AdminAuth><AdminLinkAudit /></AdminAuth>} />
              <Route path="/admin/gallery-credit-audit" element={<AdminAuth><AdminGalleryCreditAudit /></AdminAuth>} />
              <Route path="/admin/broken-links" element={<AdminBrokenLinks />} />
              <Route path="/admin/not-found-log" element={<AdminNotFoundLog />} />
              <Route path="/admin/duplicates" element={<AdminAuth><AdminDuplicates /></AdminAuth>} />
              <Route path="/admin/assets-coverage" element={<AdminAssetsCoverage />} />
              <Route path="/admin/merge-log" element={<AdminAuth><AdminMergeLog /></AdminAuth>} />
              <Route path="/admin/artifact-dedup-debug" element={<AdminAuth><AdminArtifactDedupDebug /></AdminAuth>} />
              <Route path="/admin/qr-labels" element={<AdminQrLabels />} />
              <Route path="/admin/auction-firearm-links" element={<AdminAuctionFirearmLinks />} />
              <Route path="/admin/auction-firearm-review" element={<AdminAuctionFirearmReview />} />
              <Route path="/admin/longhorn-match-audit" element={<AdminAuth><AdminLonghornMatchAudit /></AdminAuth>} />
              <Route path="/admin/collection-match-audit" element={<AdminAuth><AdminCollectionMatchAudit /></AdminAuth>} />
              <Route path="/admin/firearm-exhibit-links" element={<AdminAuth><AdminFirearmExhibitLinks /></AdminAuth>} />
              <Route path="/admin/false-positives" element={<AdminAuth><AdminFalsePositives /></AdminAuth>} />
              <Route path="/admin/review-queue" element={<AdminAuth><AdminReviewQueue /></AdminAuth>} />
              <Route path="/admin/tag-conflicts" element={<AdminAuth><AdminTagConflicts /></AdminAuth>} />
              <Route path="/admin/heritage" element={<AdminAuth><AdminHeritageEditor /></AdminAuth>} />
              <Route path="/admin/facebook-import" element={<AdminAuth><AdminFacebookImport /></AdminAuth>} />
              <Route path="/admin/gallery-import-audit" element={<AdminAuth><AdminGalleryImportAudit /></AdminAuth>} />
              <Route path="/admin/photo-upload" element={<AdminAuth><AdminPhotoUpload /></AdminAuth>} />
              <Route path="/admin/cms" element={<AdminAuth><AdminCmsEditor /></AdminAuth>} />
              <Route path="/admin/ocr-review" element={<AdminOcrReview />} />
              <Route path="/admin/ocr-jobs" element={<AdminAuth><AdminOcrJobDashboard /></AdminAuth>} />
              <Route path="/admin/ocr-jobs/:jobId" element={<AdminAuth><AdminOcrJobDetail /></AdminAuth>} />
              <Route path="/admin/ocr-quality-audit" element={<AdminOcrQualityAudit />} />
              <Route path="/admin/ocr-requeue" element={<AdminOcrRequeue />} />
              <Route path="/admin/ledger-review" element={<AdminLedgerReview />} />
              <Route path="/admin/acquisitions" element={<AdminAcquisitions />} />
              <Route path="/admin/acquisitions/flag-queue" element={<AdminAcquisitionsFlagQueue />} />
              <Route path="/admin/acquisitions/bulk-corrections" element={<AdminAcquisitionsBulkCorrections />} />
              <Route path="/admin/acquisitions/auto-image-backfill" element={<AdminAutoImageBackfill />} />
              <Route path="/admin/acquisitions/missing-photos" element={<AdminMissingPhotosQueue />} />
              <Route path="/documents" element={<Navigate to="/document-search" replace />} />
              <Route path="/doc-search" element={<Navigate to="/document-search" replace />} />
              <Route path="/gallery" element={<Navigate to="/historical-gallery" replace />} />
              <Route path="/rangers" element={<Navigate to="/texas-rangers" replace />} />
              <Route path="/longhorns" element={<Navigate to="/texas-longhorn-heritage" replace />} />
              {/* Legacy slug redirects — preserve inbound links from older URLs.
                  Keep in sync with ROUTE_REDIRECTS in src/utils/route-registry.ts. */}
              <Route path="/firearms-collection" element={<Navigate to="/collections/firearms" replace />} />
              <Route path="/ranger-firearms-evolution" element={<Navigate to="/collections/firearms-evolution" replace />} />
              <Route path="/tlj-yo-ranch-article" element={<Navigate to="/literary-legacy/tlj-yo-ranch" replace />} />
              <Route path="/yo-once-in-a-while-publication" element={<Navigate to="/literary-legacy/yo-once-in-a-while" replace />} />
              <Route path="/rangelands-yo-ranch-1984" element={<Navigate to="/literary-legacy/rangelands-yo-ranch-1984" replace />} />
              <Route path="/charles-schreiner-iii" element={<Navigate to="/schreiner-iii" replace />} />
              {/* Broken-nav backfill — preserve inbound links from old URLs, typos,
                  and external references picked up in the not_found_hits log. Keep in
                  sync with ROUTE_REDIRECTS in src/utils/route-registry.ts. */}
              <Route path="/yo-historical-archive" element={<Navigate to="/archive" replace />} />
              <Route path="/admin/appointment-requests" element={<Navigate to="/admin/appointments" replace />} />
              <Route path="/ranger-krumnow" element={<Navigate to="/texas-rangers/krumnow" replace />} />
              <Route path="/topics/texas-rangers" element={<Navigate to="/texas-rangers" replace />} />
              <Route path="/topics/texas-longhorns" element={<Navigate to="/texas-longhorn-heritage" replace />} />
              <Route path="/topics/famous-visitors" element={<Navigate to="/famous-visitors" replace />} />
              <Route path="/topics/exotic-wildlife-heritage" element={<Navigate to="/wildlife-heritage" replace />} />
              <Route path="/texas-rangers/captain-aldrich" element={<Navigate to="/captain-aldrich" replace />} />
              <Route path="/texas-rangers/arrington" element={<Navigate to="/texas-rangers" replace />} />
              <Route path="/texas-rangers/peoples" element={<Navigate to="/texas-rangers" replace />} />
              <Route path="/longhorn-preservation" element={<Navigate to="/longhorn-preservation-pioneers" replace />} />
              <Route path="/texas-longhorns" element={<Navigate to="/texas-longhorn-heritage" replace />} />
              <Route path="/hollywood-and-media" element={<Navigate to="/hollywood-media" replace />} />
              <Route path="/culture-fame" element={<Navigate to="/culture-and-fame" replace />} />
              <Route path="/ranch-found-photos" element={<Navigate to="/ranch-found-photographs" replace />} />
              <Route path="/collections/historic-firearms" element={<Navigate to="/collections/firearms" replace />} />
              <Route path="/collections/texas-ranger-artifacts" element={<Navigate to="/collections/ranger" replace />} />
              <Route path="/collections/spurs-leather-frontier" element={<Navigate to="/explore?view=types" replace />} />
             <Route path="*" element={<Layout><NotFound /></Layout>} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
